import { useRef, useState, useCallback, useEffect } from 'react';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';

function useAudioRecorder(errorHandler) {
    const streamRef = useRef();
    const audioRecorderRef = useRef();
    const [recorderStatus, setRecorderStatus] = useState(window.navigator.mediaDevices ? 'idle' : 'no-media-devices');
    const initialize = useCallback(async () => {
        try {
            const mediaStream = await window.navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            streamRef.current = mediaStream;
            const options = {
                type: 'audio',
                recorderType: StereoAudioRecorder,
                numberOfAudioChannels: 2,
            };
            audioRecorderRef.current = new RecordRTC(mediaStream, options);
            return true;
        }
        catch (error) {
            if (error instanceof DOMException) {
                if (error.name === 'NotAllowedError')
                    setRecorderStatus('access-denied');
                if (error.name === 'NotFoundError')
                    setRecorderStatus('no-microphone');
            }
            else {
                setRecorderStatus('error');
                errorHandler(error);
            }
            return false;
        }
    }, [errorHandler]);
    const start = useCallback(async () => {
        const initialized = await initialize();
        if (!initialized)
            return false;
        const { current: audioRecorder } = audioRecorderRef;
        if (!audioRecorder)
            return false;
        setRecorderStatus('recording');
        audioRecorder.reset();
        audioRecorder.startRecording();
        return true;
    }, [initialize]);
    const clearRecordingIndicator = useCallback(() => {
        const { current: stream } = streamRef;
        if (stream)
            stream.getTracks().forEach(track => {
                track.stop();
            });
    }, []);
    const stop = useCallback(async () => {
        const { current: audioRecorder } = audioRecorderRef;
        if (!audioRecorder)
            throw new Error('No audioRecorder started.');
        await new Promise(resolve => audioRecorder.stopRecording(resolve));
        clearRecordingIndicator();
        const blob = audioRecorder.getBlob();
        setRecorderStatus('idle');
        return window.URL.createObjectURL(blob);
    }, [clearRecordingIndicator]);
    const audioRecorder = {
        status: recorderStatus,
        start,
        stop,
    };
    useEffect(() => {
        return () => clearRecordingIndicator();
    }, [clearRecordingIndicator]);
    return audioRecorder;
}

export { useAudioRecorder };
